import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";

import type { ReactNode } from "react";

interface DeleteModalProps {
  /** The entity name displayed in the title and confirmation text (e.g. "Mailing List", "Member") */
  name: string;
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close (cancel or backdrop click) */
  onClose: () => void;
  /** Called when the user confirms deletion */
  onDelete: () => void;
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
  /** Whether content is still loading */
  isLoading?: boolean;
  /** RTK Query error object to display */
  error?: FetchBaseQueryError;
  /** Content rendered in the center of the modal (typically a Card with entity details) */
  children?: ReactNode;
}

export default function DeleteModal({
  name,
  open,
  onClose,
  onDelete,
  isDeleting = false,
  isLoading = false,
  error,
  children,
}: DeleteModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <DeleteOutlineIcon color="error" />
        Delete {name}
      </DialogTitle>

      <DialogContent>
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this {name.toLowerCase()}?{" "}
            <strong>This action cannot be undone.</strong>
          </Typography>

          {children}
        </Box>
      </DialogContent>

      <DialogActions sx={{ mb: 2, mx: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mr: "auto" }}>
            {normalizeErrorMessages(error)}
          </Alert>
        )}
        <Button variant="outlined" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onDelete}
          disabled={isDeleting || isLoading}
          loading={isDeleting}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
